import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import processApi, { HostProcess, ProcessQueryOptions } from '../../services/api/process.api';

interface ProcessListProps {
  hostId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export default function ProcessList({ hostId, autoRefresh = false, refreshInterval = 30000 }: ProcessListProps) {
  const { t } = useTranslation(['infrastructure', 'common']);
  const [processes, setProcesses] = useState<HostProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [sortBy, setSortBy] = useState<'cpu' | 'memory' | 'name' | 'pid'>('cpu');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const loadProcesses = useCallback(async () => {
    try {
      setLoading(true);
      const options: ProcessQueryOptions = {
        sortBy,
        sortOrder,
        limit,
        offset: page * limit,
      };

      if (search) {
        options.search = search;
      }

      if (statusFilter) {
        options.status = statusFilter;
      }

      const result = await processApi.getHostProcesses(hostId, options);
      setProcesses(result.data);
      setTotal(result.meta.total);
    } catch (error) {
      console.error('Error loading processes:', error);
    } finally {
      setLoading(false);
    }
  }, [hostId, sortBy, sortOrder, limit, page, search, statusFilter]);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadProcesses();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadProcesses]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      running: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      sleeping: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      stopped: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      zombie: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      idle: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      'disk-sleep': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };

    return (
      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || colors.unknown}`}>
        {status}
      </span>
    );
  };

  const getCpuColor = (cpu: number): string => {
    if (cpu >= 80) return 'text-red-600 dark:text-red-400';
    if (cpu >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getMemoryColor = (memory: number): string => {
    if (memory >= 80) return 'text-red-600 dark:text-red-400';
    if (memory >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const handleSort = (field: 'cpu' | 'memory' | 'name' | 'pid') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder={t('infrastructure:processes.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="">{t('infrastructure:processes.allStatuses')}</option>
          <option value="running">{t('infrastructure:processes.statusRunning')}</option>
          <option value="sleeping">{t('infrastructure:processes.statusSleeping')}</option>
          <option value="stopped">{t('infrastructure:processes.statusStopped')}</option>
          <option value="zombie">{t('infrastructure:processes.statusZombie')}</option>
        </select>

        <button
          onClick={loadProcesses}
          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Process Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleSort('pid')}
              >
                <div className="flex items-center gap-1">
                  PID
                  {sortBy === 'pid' && (
                    <span>{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  {t('infrastructure:processes.name')}
                  {sortBy === 'name' && (
                    <span>{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('infrastructure:processes.user')}
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('infrastructure:processes.status')}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleSort('cpu')}
              >
                <div className="flex items-center gap-1">
                  CPU%
                  {sortBy === 'cpu' && (
                    <span>{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleSort('memory')}
              >
                <div className="flex items-center gap-1">
                  {t('infrastructure:processes.memory')}
                  {sortBy === 'memory' && (
                    <span>{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('infrastructure:processes.threads')}
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('infrastructure:processes.command')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">{t('common:loading')}</span>
                  </div>
                </td>
              </tr>
            ) : processes.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  {t('infrastructure:processes.noProcesses')}
                </td>
              </tr>
            ) : (
              processes.map((process) => (
                <tr key={process.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                    {process.pid}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {process.name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {process.username || '-'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getStatusBadge(process.status)}
                  </td>
                  <td className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${getCpuColor(process.cpu_percent)}`}>
                    {process.cpu_percent.toFixed(1)}%
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span className={`font-medium ${getMemoryColor(process.memory_percent)}`}>
                      {process.memory_percent.toFixed(1)}%
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-1">
                      ({formatBytes(process.memory_rss)})
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {process.num_threads}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={process.command}>
                    {process.command || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between px-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('infrastructure:processes.showing', {
              start: page * limit + 1,
              end: Math.min((page + 1) * limit, total),
              total,
            })}
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {t('common:previous')}
            </button>

            <span className="text-sm text-gray-500 dark:text-gray-400">
              {page + 1} / {totalPages}
            </span>

            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {t('common:next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
